/**
 * Match an Explore map bank label to machines the same way search groups banks.
 *
 * Primary key: section+bank via resolveSectionBank (floorLocation first, then
 * machine section/bank fields) over GetSearchData / casinoSearchData rows —
 * includes machines with no play in the selected timeframe.
 *
 * Fallback for non-insight / unresolved rows: area+section (and bare section)
 * so legacy floors still match when floorLocation does not encode a bank.
 */

import { ISearchInfo } from 'appRedux/models/casinoModels';
import {
  floorLocationShortLabel,
  resolveSectionBank,
  type InsightSearchableMachine,
} from './insightSearchMatch';

export type BankLabelMatchMachine = InsightSearchableMachine & {
  area?: string | null;
  section?: string | null;
  bank?: string | null;
  mnum?: string | number | null;
  slot_number?: string | number | null;
};

/**
 * Same rule as searchRowDetailId (insightCasino): prefer numeric mnum, else
 * numeric slot_number. Kept local so unit tests do not pull the full insight
 * service / config graph.
 */
function machineDetailId(row: {
  mnum?: unknown;
  slot_number?: unknown;
} | null | undefined): number | null {
  if (!row) return null;
  if (row.mnum !== null && row.mnum !== undefined && String(row.mnum).trim() !== '') {
    const n = Number(String(row.mnum).trim());
    return Number.isFinite(n) ? n : null;
  }
  if (row.slot_number === null || row.slot_number === undefined || row.slot_number === '') {
    return null;
  }
  const n = Number(row.slot_number);
  return Number.isFinite(n) ? n : null;
}

export type BankLabelMatch = {
  /** Normalized label key (no spaces, no BANK prefix). */
  key: string;
  /** Chip / SelectedSlotsName text, e.g. "Bank 9G". */
  displayLabel: string;
  /** Machine ids for SelectedSlots (mnum when present). */
  mnums: string[];
};

/**
 * Normalize a map/search bank label for comparison:
 * trim → uppercase → strip spaces → strip leading "BANK".
 */
export function normalizeBankLabel(raw: string | null | undefined): string {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/^BANK/, '');
}

/** True when the active bank filter (chip) refers to the same bank as `label`. */
export function isActiveBankFilterLabel(
  activeName: string | null | undefined,
  isBankFilter: boolean,
  label: string | null | undefined
): boolean {
  if (!isBankFilter) return false;
  const active = normalizeBankLabel(activeName);
  const tapped = normalizeBankLabel(label);
  return Boolean(active) && Boolean(tapped) && active === tapped;
}

function addKey(keys: Set<string>, value: string | null | undefined): void {
  const n = normalizeBankLabel(value);
  if (n) keys.add(n);
}

/**
 * All normalized keys that can identify this machine's bank for a label tap.
 */
export function machineBankLabelKeys(machine: BankLabelMatchMachine): string[] {
  const keys = new Set<string>();
  const resolved = resolveSectionBank(machine);
  addKey(keys, `${resolved.section}${resolved.bank}`);
  addKey(keys, floorLocationShortLabel(machine));

  // Non-insight / unresolved fallback: area+section (and section alone so
  // Eureka-style labels that omit a numeric area prefix still hit).
  const area = String(machine.area ?? '').trim();
  const section = String(machine.section ?? '').trim();
  if (area || section) {
    addKey(keys, `${area}${section}`);
    addKey(keys, section);
  }

  return Array.from(keys);
}

function machineMnumString(machine: BankLabelMatchMachine): string | null {
  const id = machineDetailId(machine);
  return id !== null ? String(id) : null;
}

/**
 * Find machines whose bank matches the map label.
 * Returns null when the label is not a bank (ATM / Kiosk / Cashier / unknown).
 */
export function matchBankLabel(
  label: string | null | undefined,
  machines: BankLabelMatchMachine[] | null | undefined
): BankLabelMatch | null {
  const key = normalizeBankLabel(label);
  if (!key || !Array.isArray(machines) || machines.length === 0) return null;

  const matched = machines.filter((m) => machineBankLabelKeys(m).includes(key));
  if (matched.length === 0) return null;

  const mnums: string[] = [];
  const seen = new Set<string>();
  for (const m of matched) {
    const id = machineMnumString(m);
    if (id === null || seen.has(id)) continue;
    seen.add(id);
    mnums.push(id);
  }
  if (mnums.length === 0) return null;

  // Prefer search's Bank section label so the chip matches "Bank 9G".
  const displayLabel = floorLocationShortLabel(matched[0]);
  const safeDisplay =
    displayLabel && normalizeBankLabel(displayLabel)
      ? displayLabel
      : `Bank ${key}`;

  return { key, displayLabel: safeDisplay, mnums };
}

/** Convenience: match against GetSearchData rows. */
export function matchBankLabelInSearchData(
  label: string | null | undefined,
  searchRows: ISearchInfo[] | null | undefined
): BankLabelMatch | null {
  return matchBankLabel(label, searchRows as BankLabelMatchMachine[] | null | undefined);
}
